// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect } from 'react';
import { models } from 'powerbi-client';
import { PowerBIEmbed } from 'powerbi-client-react';
import 'powerbi-report-authoring';
import { sampleReportUrl } from './public/constants';
import './DemoApp.css';
// Root Component to demonstrate usage of embedded component
function DemoApp() {
    // PowerBI Report object (to be received via callback)
    const [report, setReport] = useState();
    // Track Report embedding status
    const [isEmbedded, setIsEmbedded] = useState(false);
    // Overall status message of embedding
    const [displayMessage, setMessage] = useState(`The report is bootstrapped. Click the Embed Report button to set the access token`);
    // CSS Class to be passed to the embedded component
    const reportClass = 'report-container';
    // Pass the basic embed configurations to the embedded component to bootstrap the report on first load
    // Values for properties like embedUrl, accessToken and settings will be set on click of button
    const [sampleReportConfig, setReportConfig] = useState({
        type: 'report',
        embedUrl: undefined,
        tokenType: models.TokenType.Embed,
        accessToken: undefined,
        settings: undefined,
    });
    /**
     * Map of event handlers to be applied to the embedded report
     * Update event handlers for the report by redefining the map using the setEventHandlersMap function
     * Set event handler to null if event needs to be removed
     * More events can be provided from here
     * https://docs.microsoft.com/en-us/javascript/api/overview/powerbi/handle-events#report-events
     */
    const [eventHandlersMap, setEventHandlersMap] = useState(new Map([
        ['loaded', () => console.log('Report has loaded')],
        ['rendered', () => console.log('Report has rendered')],
        ['error', (event) => {
                if (event) {
                    console.error(event.detail);
                }
            },
        ],
        ['visualClicked', () => console.log('visual clicked')],
        ['pageChanged', (event) => console.log(event)],
    ]));
    useEffect(() => {
        if (report) {
            report.setComponentTitle('Embedded Report');
        }
    }, [report]);
    /**
     * Embeds report
     *
     * @returns Promise<void>
     */
    const embedReport = () => __awaiter(this, void 0, void 0, function* () {
        console.log('Embed Report clicked');
        // Get the embed config from the service
        const reportConfigResponse = yield fetch(sampleReportUrl);
        if (reportConfigResponse === null) {
            return;
        }
        if (!(reportConfigResponse === null || reportConfigResponse === void 0 ? void 0 : reportConfigResponse.ok)) {
            console.error(`Failed to fetch config for report. Status: ${reportConfigResponse.status} ${reportConfigResponse.statusText}`);
            return;
        }
        const reportConfig = yield reportConfigResponse.json();
        // Update the reportConfig to embed the PowerBI report
        setReportConfig(Object.assign(Object.assign({}, sampleReportConfig), { embedUrl: reportConfig.EmbedUrl, accessToken: reportConfig.EmbedToken.Token }));
        setIsEmbedded(true);
        // Update the display message
        setMessage('Use the buttons above to interact with the report using Power BI Client APIs.');
    });
    /**
     * Hide Filter Pane
     *
     * @returns Promise<IHttpPostMessageResponse<void> | undefined>
     */
    const hideFilterPane = () => __awaiter(this, void 0, void 0, function* () {
        // Check if report is available or not
        if (!report) {
            setDisplayMessageAndConsole('Report not available');
            return;
        }
        // New settings to hide filter pane
        const settings = {
            panes: {
                filters: {
                    expanded: false,
                    visible: false,
                },
            },
        };
        try {
            const response = yield report.updateSettings(settings);
            // Update display message
            setDisplayMessageAndConsole('Filter pane is hidden.');
            return response;
        }
        catch (error) {
            console.error(error);
            return;
        }
    });
    /**
     * Set data selected event
     *
     * @returns void
     */
    const setDataSelectedEvent = () => {
        setEventHandlersMap(new Map([
            ...eventHandlersMap,
            ['dataSelected', (event) => console.log(event)],
        ]));
        setMessage('Data Selected event set successfully. Select data to see event in console.');
    };
    /**
     * Change visual type
     *
     * @returns Promise<void>
     */
    const changeVisualType = () => __awaiter(this, void 0, void 0, function* () {
        // Check if report is available or not
        if (!report) {
            setDisplayMessageAndConsole('Report not available');
            return;
        }
        // Get active page of the report
        const activePage = yield report.getActivePage();
        if (!activePage) {
            setMessage('No Active page found');
            return;
        }
        try {
            // Change the visual type using powerbi-report-authoring
            // For more information: https://docs.microsoft.com/en-us/javascript/api/overview/powerbi/report-authoring-overview
            const visual = yield activePage.getVisualByName('VisualContainer6');
            const response = yield visual.changeType('lineChart');
            setDisplayMessageAndConsole(`The ${visual.type} was updated to lineChart.`);
            return response;
        }
        catch (error) {
            if (error === 'PowerBIEntityNotFound') {
                console.log('No Visual found with that name');
            }
            else {
                console.log(error);
            }
        }
    });
    /**
     * Set display message and log it in the console
     *
     * @returns void
     */
    const setDisplayMessageAndConsole = (message) => {
        setMessage(message);
        console.log(message);
    };
    const controlButtons = isEmbedded ?
        React.createElement(React.Fragment, null,
            React.createElement("button", { onClick: changeVisualType }, "Change visual type"),
            React.createElement("button", { onClick: hideFilterPane }, "Hide filter pane"),
            React.createElement("button", { onClick: setDataSelectedEvent }, "Set event"),
            React.createElement("label", { className: "display-message" }, displayMessage))
        :
            React.createElement(React.Fragment, null,
                React.createElement("label", { className: "display-message position" }, displayMessage),
                React.createElement("button", { onClick: embedReport, className: "embed-report" }, "Embed Report"));
    const header = React.createElement("div", { className: "header" }, "Apogee Metrics Dashboard Demo");
    const reportComponent = React.createElement(PowerBIEmbed, { embedConfig: sampleReportConfig, eventHandlers: eventHandlersMap, cssClassName: reportClass, getEmbeddedComponent: (embedObject) => {
            console.log(`Embedded object of type "${embedObject.embedtype}" received`);
            setReport(embedObject);
        } });
    const footer = React.createElement("div", { className: "footer" },
        React.createElement("p", null, "This demo is powered by Power BI & Apogee Media "),
        React.createElement("label", { className: "separator-pipe" }, "|"));
    return (React.createElement("div", { className: "container" },
        header,
        React.createElement("div", { className: "controls" },
            controlButtons,
            isEmbedded ? reportComponent : null),
        footer));
}
export default DemoApp;
